// src/components/cognition/tables/competitive-comparison-table.tsx
import React from "react";

function levelBadge(level: string) {
  const v = String(level || "").toLowerCase();

  if (v === "high") {
    return { bg: "var(--alpha-teal-5)", border: "var(--alpha-teal-30)", text: "var(--alpha-teal)" };
  }
  if (v === "moderate" || v === "medium") {
    return { bg: "rgba(245, 158, 11, 0.10)", border: "rgba(245, 158, 11, 0.35)", text: "rgba(245, 158, 11, 1)" };
  }
  if (v === "low") {
    return { bg: "var(--infrared-coral-5)", border: "var(--infrared-coral-30)", text: "var(--infrared-coral)" };
  }
  return { bg: "var(--muted)", border: "var(--border)", text: "var(--muted-foreground)" };
}

function Badge({ value }: { value: string }) {
  const st = levelBadge(value);
  const label = value ? value[0].toUpperCase() + value.slice(1) : "—";
  return (
    <span
      className="inline-flex items-center px-2 py-1 rounded"
      style={{
        backgroundColor: st.bg,
        border: `1px solid ${st.border}`,
        color: st.text,
        fontSize: "var(--text-sm)",
        fontWeight: "var(--font-weight-semibold)",
        justifyContent: "center",
        minWidth: 90,
      }}
    >
      {label}
    </span>
  );
}

export function CompetitiveComparisonTable({
  rows,
}: {
  rows: Array<{
    product: string;
    overallScore?: number;
    overall?: number;
    performance: string;
    value: string;
    features: string;
  }>;
}) {
  if (!rows?.length) return null;

  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr>
            <th
              className="px-4 py-3 text-left"
              style={{
                backgroundColor: "var(--alpha-teal-10)",
                borderTop: "1px solid var(--border)",
                borderLeft: "1px solid var(--border)",
                borderTopLeftRadius: "var(--radius)",
                fontWeight: "var(--font-weight-semibold)",
              }}
            >
              Product
            </th>
            {["Overall Score", "Performance", "Value", "Features"].map((h, i) => (
              <th
                key={h}
                className="px-4 py-3 text-center"
                style={{
                  backgroundColor: "var(--alpha-teal-10)",
                  borderTop: "1px solid var(--border)",
                  borderLeft: "1px solid var(--border)",
                  borderRight: i === 3 ? "1px solid var(--border)" : undefined,
                  borderTopRightRadius: i === 3 ? "var(--radius)" : undefined,
                  fontWeight: "var(--font-weight-semibold)",
                }}
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {rows.map((r, idx) => {
            const score = r.overallScore ?? r.overall ?? null;
            return (
              <tr key={`${r.product}-${idx}`}>
                <td className="px-4 py-3" style={{ borderLeft: "1px solid var(--border)", borderTop: "1px solid var(--border)" }}>
                  {r.product}
                </td>
                <td className="px-4 py-3 text-center" style={{ borderLeft: "1px solid var(--border)", borderTop: "1px solid var(--border)" }}>
                  {score == null ? "—" : Number(score).toFixed(1)}
                </td>
                <td className="px-4 py-3 text-center" style={{ borderLeft: "1px solid var(--border)", borderTop: "1px solid var(--border)" }}>
                  <Badge value={r.performance} />
                </td>
                <td className="px-4 py-3 text-center" style={{ borderLeft: "1px solid var(--border)", borderTop: "1px solid var(--border)" }}>
                  <Badge value={r.value} />
                </td>
                <td className="px-4 py-3 text-center" style={{ borderLeft: "1px solid var(--border)", borderTop: "1px solid var(--border)", borderRight: "1px solid var(--border)" }}>
                  <Badge value={r.features} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
